import React from 'react';
import { flushSync } from 'react-dom';
import { Button, Spin } from 'antd';
import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useModel } from '@umijs/max';
import { loginOut } from '@/components/RightContent/AvatarDropdown';
import { dtFormat } from '@/util';
import ImpersonationBanner from '@/components/ImpersonationBanner';

/**
 * Full-screen lock for users with clockin.required: until they clock in the
 * page content is not mounted at all (so it makes no API calls), and the
 * overlay covers the header/menu too. The backend enforces the same rule
 * (ClockInGuard), so this is the UX side, not the security boundary.
 */
const ClockInGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const { mustClockIn, loaded, locked, autoCloseNotice, busy, dismissingNotice, clockIn, dismissAutoClose } =
    useModel('clockGate');

  if (!mustClockIn) return <>{children}</>;

  if (!loaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!locked) return <>{children}</>;

  const firstName = (initialState?.currentUser?.display_name || initialState?.currentUser?.user_name || '')
    .trim()
    .split(' ')[0];

  const handleClockIn = async () => {
    if (autoCloseNotice) await dismissAutoClose();
    await clockIn();
  };

  const handleLogout = () => {
    flushSync(() => {
      setInitialState((s) => ({ ...s, currentUser: undefined }));
    });
    loginOut();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: '#f5f7fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      {/* This overlay covers the layout's banner — keep "Logged in as …" visible (renders nothing otherwise). */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
        <ImpersonationBanner />
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.08)',
          padding: '36px 28px 28px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: autoCloseNotice ? '#fff7e6' : '#e6f4ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          {autoCloseNotice ? (
            <ExclamationCircleOutlined style={{ fontSize: 30, color: '#fa8c16' }} />
          ) : (
            <ClockCircleOutlined style={{ fontSize: 30, color: '#1677ff' }} />
          )}
        </div>
        {firstName && (
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: autoCloseNotice ? '#fa8c16' : '#1677ff',
              marginBottom: 4,
            }}
          >
            Hi {firstName},
          </div>
        )}
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          {autoCloseNotice ? 'You were clocked out automatically' : 'Clock in to start working'}
        </div>
        <div style={{ color: '#8c8c8c', marginBottom: 28 }}>
          {autoCloseNotice ? (
            <>
              Your session from {dtFormat(autoCloseNotice.clocked_in_at, 'h:mm A')} to{' '}
              {dtFormat(autoCloseNotice.clocked_out_at, 'h:mm A')} was closed because no activity was detected.
              Clock back in to continue.
            </>
          ) : (
            'You need to be clocked in to use the system.'
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button onClick={handleLogout}>Log out</Button>
          <Button type="primary" loading={busy || dismissingNotice} onClick={handleClockIn}>
            Clock In
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClockInGate;
